import { PATIENT_RETURN_FRAGMENT } from '@/entities/patient-return/@x/study';
import { gql } from '@apollo/client';
import DIAGNOSIS_FRAGMENT from './diagnosisFragment';
import INSURANCE_FRAGMENT from './insuranceFragment';
import PATIENT_FRAGMENT from './patientFragment';
import PATIENT_ONBOARDING_FRAGMENT from './patientOnBoardingFragment';
import PHYSICIAN_FRAGMENT from './physicianFragment';
import LABEL_FRAGMENT from './labels';
import STUDY_SETTING_FRAGMENT from './studySettingFragment';

const STUDY_INFO_CLINIC_FRAGMENT = gql`
  fragment StudyInfoClinicFragment on Study {
    id
    friendlyId
    lowCellSignal
    rma
    referenceCode
    sentReferenceCodeAt
    artifact {
      resolvedAt
      percentage
      lastIssueFound
      commonIssueFound
      shouldBeResolved
    }
    onboarding {
      ...PatientOnBoardingFragment
    }
    info {
      insurance {
        ...InsuranceFragment
      }
      patient {
        ...PatientFragment
      }
      interprettingPhysician {
        ...PhysicianFragment
      }
      referringPhysician {
        ...PhysicianFragment
      }
      diagnosis {
        ...DiagnosisFragment
      }
      followOnStudy {
        duration
        studyType
        reportDueDate
        cancelledReason
      }
      clinicianInitials
      studySetting {
        ...StudySettingFragment
      }
    }
    studyHistory {
      time
      batteryLevel
      status
    }
    deviceNotifications {
      time
      batteryLevel
      status
    }
    reports {
      id
      reportId
      type
      createdAt
      start
      stop
      status
      statusReport
      lastStatusReportAt
      event {
        friendlyId
        id
        hasClinicData
      }
      technicianComments
      physicianInterpretation
      lastModified
      isRenderingBackground
      inbox {
        date
        priority
        isReviewed
        isRead
      }
      isArtifactReport
      isNewFormat
      formatVersion
      aiFormatVersion
      profile
    }
    pendingCommand
    start
    stop
    studyType
    status
    reportDueDate
    studyNote
    timezone
    patientFirstSyncDeviceAt
    isArchived
    initialDurationOfStudy
    studyTimeOffset
    initDuration
    linkedStudies {
      id
      friendlyId
      initDuration
      reportDueDate
      studyType
      patientFirstSyncDeviceAt
      patientReturn {
        id
      }
    }
    studySetting {
      ...StudySettingFragment
    }
    facility {
      id
      name
      contact {
        address
        city
        state
        country
        zip
        phone1
        phone2
      }
      studySetting {
        preEventTime
        postEventTime
        bradycardiaThreshold
        tachycardiaThreshold
        pauseLevel
        pauseDetection
        afibDetection
        diagnosticLead
      }
      status
      arrears
      canViewFlaggedStudies
      timezone
      enableLiveECGStream
      amplifyEcgSignal {
        biotresProDeviceRate
        biotresDeviceRate
        biofluxDeviceRate
      }
    }
    device {
      id
      deviceId
      status
      firstStudyDate
      lastSync {
        time
        operator
        networkMode
        gprs
        schema
      }
      facility {
        id
        name
        status
      }
      model
      canRunBiotresMCT
    }
    isBiofluxDirect
    biofluxDirect {
      id
      senderInformation {
        id
        latestLabel {
          ...LabelFragment
        }
      }
      recipientInformation {
        id
        latestLabel {
          ...LabelFragment
        }
      }
    }
    isTest
    patientReturn {
      ...PatientReturnFragment
    }
    ecgDetail
    patientPaired
    orderNumber
    isAborting
    isDeviceAssignmentPending
    initiatedTime
    flaggedStudy {
      reason
      resolvedAt
      resolvedBy {
        id
      }
    }
    flaggedStudySkip {
      ongoing {
        skippedAt
      }
      endOfDuration {
        skippedAt
      }
    }
    isNonPhi
    isStopping
    lastAddressedTime
    externalSource {
      provider
      externalId
    }
    deviceType
    # isExternalDevice
  }

  ${PATIENT_FRAGMENT}
  ${LABEL_FRAGMENT}
  ${DIAGNOSIS_FRAGMENT}
  ${INSURANCE_FRAGMENT}
  ${PATIENT_ONBOARDING_FRAGMENT}
  ${PATIENT_RETURN_FRAGMENT}
  ${PHYSICIAN_FRAGMENT}
  ${STUDY_SETTING_FRAGMENT}
`;

export default STUDY_INFO_CLINIC_FRAGMENT;
